import Modal from '@/src/Common/components/Modal';
import useShare from '@/src/Share/components/hooks/useShare';
import { Plus } from 'lucide-react';
import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/src/shadComponents/ui/button';
import { Input } from '@/src/shadComponents/ui/input';
import { Label } from '@/src/shadComponents/ui/label';

export default function SharePrompt({
    shareIdentifiers,
    activationId,
    galleryId,
    onComplete,
}: {
    shareIdentifiers: string[];
    activationId: number;
    galleryId: number;
    onComplete: () => void;
}) {
    const cancelButtonRef = useRef(null);
    const [newDestination, setNewDestination] = useState('');
    const [isSending, setIsSending] = useState(false);
    const { createShare } = useShare();

    const createZipAndSend = async () => {
        if (!newDestination) {
            return;
        }

        await createShare({
            destination: newDestination,
            shareIdentifiers,
            activationId,
            galleryId,
            type: newDestination.includes('@') ? 0 : 1,
        });

        setIsSending(true);

        await toast.success('Shares Sent to ' + newDestination);
        setIsSending(false);
        onComplete();
    };

    return (
        <Modal cancelButtonRef={cancelButtonRef} onDismiss={onComplete}>
            <div className="px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary sm:mx-0">
                        <Plus
                            className="size-5 text-primary-foreground"
                            aria-hidden="true"
                        />
                    </div>
                    <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                        <h3 className="text-base font-semibold text-foreground">
                            Send Shares to Email or Text
                        </h3>
                        <p className="mt-2 text-sm text-muted-foreground">
                            Sends {shareIdentifiers.length} photos to
                            the email or text message you provide and
                            notify the recipient. Will only send a
                            single message, but all photos will be added
                            to recipient's gallery.
                        </p>
                    </div>
                </div>
                <div className="mt-4 sm:ml-14">
                    <div className="space-y-2">
                        <Label htmlFor="destination">
                            Email / Phone
                        </Label>
                        <Input
                            id="destination"
                            name="destination"
                            type="text"
                            required
                            value={newDestination}
                            onChange={(e) =>
                                setNewDestination(e.target.value)
                            }
                            placeholder="Enter email or phone number"
                        />
                    </div>
                </div>
            </div>
            <div className="flex justify-end gap-3 px-4 py-3 border-t">
                <Button
                    type="button"
                    variant="outline"
                    onClick={() => onComplete()}
                    ref={cancelButtonRef}
                >
                    Cancel
                </Button>
                <Button
                    onClick={createZipAndSend}
                    disabled={isSending}
                >
                    {isSending ? 'Sending...' : 'Send Shares'}
                </Button>
            </div>
        </Modal>
    );
}
